import React from "react";
import { Link, useLocation } from "react-router-dom";
import { instruments, levels } from "../mock/mockData";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Music4, ArrowLeft } from "lucide-react";
import { InstrumentIcon } from "../components/common/InstrumentIcon";
import { useCurrency, BASE_EXAM_FEE_EUR, PREMIUM_CERT_FEE_EUR } from "../context/CurrencyContext";

export default function InstrumentsLevels() {
  const location = useLocation();
  const fromDashboard = location.state?.fromDashboard || document.referrer.includes('/dashboard');
  const { formatPrice } = useCurrency();

  // Helper to get exam fee based on instrument type
  const getExamFee = (instrumentId) => {
    if (instrumentId === "ai-voice" || instrumentId === "music-therapy") {
      return formatPrice(PREMIUM_CERT_FEE_EUR);
    }
    return formatPrice(BASE_EXAM_FEE_EUR);
  };

  return (
    <div className="space-y-8">
      {fromDashboard && (
        <Link to="/dashboard">
          <Button variant="outline" size="sm" className="border-[#d5c39d] text-[#182033] hover:bg-[#f5ebd9]">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </Link>
      )}

      <section className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Instruments &amp; Levels</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-slate-700 sm:text-base">
          Global Music Conservatory offers examinations across a range of instruments. Each instrument syllabus is structured from Level 1 through Level 6, with consistent expectations for musical preparation and assessment.
        </p>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold tracking-tight text-slate-900">Instruments offered</h2>
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sky-50 text-sky-800">
            <Music4 className="h-3.5 w-3.5" />
          </span>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {instruments.map((instrument) => {
            const isGuitar = instrument.id === "guitar";
            const isPiano = instrument.id === "piano";
            const isDrums = instrument.id === "drums";
            const isViolin = instrument.id === "violin";
            const isVocals = instrument.id === "voice";
            const isAltoSaxophone = instrument.id === "alto-saxophone";
            const isAIVoice = instrument.id === "ai-voice";
            const isMusicTherapy = instrument.id === "music-therapy";
            const targetHref = isGuitar
              ? "/guitar-syllabus"
              : isPiano
              ? "/piano-syllabus"
              : isDrums
              ? "/drums-syllabus"
              : isViolin
              ? "/violin-syllabus"
              : isVocals
              ? "/vocals-syllabus"
              : isAltoSaxophone
              ? "/alto-saxophone-syllabus"
              : isAIVoice
              ? "/ai-voice-syllabus"
              : isMusicTherapy
              ? "/music-therapy-syllabus"
              : `/instruments/${instrument.id}`;

            return (
              <Link
                key={instrument.id}
                to={targetHref}
                className="group block rounded-2xl border border-slate-200 bg-white/80 shadow-sm backdrop-blur-xl transition hover:-translate-y-0.5 hover:border-amber-400/70 hover:shadow-md"
              >
                <Card className="border-0 bg-transparent shadow-none">
                  <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
                    <div>
                      <CardTitle className="text-sm font-semibold text-slate-900 group-hover:text-amber-800">
                        {instrument.name}
                      </CardTitle>
                      <p className="text-xs text-slate-500">{instrument.category}</p>
                    </div>
                    <InstrumentIcon id={instrument.id} />
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-700">{instrument.description}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-xs font-semibold text-amber-700 group-hover:text-amber-800">
                        {isGuitar
                          ? "Guitar syllabus level wise"
                          : isPiano
                          ? "Piano syllabus level wise"
                          : isDrums
                          ? "Drum kit syllabus level wise"
                          : isViolin
                          ? "Violin syllabus level wise"
                          : isVocals
                          ? "Vocal syllabus & specialisations"
                          : isAltoSaxophone
                          ? "Alto saxophone syllabus level wise"
                          : isAIVoice
                          ? "View master certification syllabus"
                          : isMusicTherapy
                          ? "View certification syllabus"
                          : "View full syllabus by level"}
                      </p>
                      <span className="inline-flex items-center rounded-full bg-[#182033] px-2.5 py-1 text-xs font-bold text-white">
                        {getExamFee(instrument.id)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-base font-semibold tracking-tight text-slate-900">Examination levels (1–6)</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {levels.map((level) => (
            <Card
              key={level.id}
              className="border-slate-200 bg-slate-50/80 shadow-sm backdrop-blur-xl"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-900">
                  {level.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700">{level.descriptor}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
